import { Fragment } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { ShoppingCartIcon, MenuIcon, XIcon } from '@heroicons/react/outline';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

const navigation = [{ name: 'Products', href: '/', current: true }];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const ShoppingIcon = ({ mobile, setOpenCart }) => {
  const { items } = useSelector((state) => state?.cartItems);
  const cartItemLength = items?.length;

  return (
    <button onClick={() => setOpenCart(true)} type="button">
      <div
        className={classNames(
          mobile ? 'px-5' : 'ml-4 md:ml-6',
          'flex items-center relative'
        )}
      >
        <div
          className={classNames(
            mobile ? 'ml-auto flex-shrink-0' : '',
            'bg-gray-800 p-1 rounded-full text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white'
          )}
        >
          <span className="sr-only">View cart</span>
          <ShoppingCartIcon className="h-6 w-6" aria-hidden="true" />
        </div>
        {cartItemLength > 0 && (
          <div
            className={classNames(
              mobile ? 'right-0' : 'left-5 ',
              'absolute top-0 rounded-full px-2 py-1 text-xs font-semibold text-white bg-sky-500'
            )}
          >
            {cartItemLength}
          </div>
        )}
      </div>
    </button>
  );
};

const NavigationLink = ({ mobile }) => {
  return (
    <>
      {navigation.map((item) => (
        <Link
          key={item.name}
          to={item.href}
          className={classNames(
            item.current
              ? 'bg-gray-900 text-white'
              : 'text-gray-300 hover:bg-gray-700 hover:text-white',
            mobile ? 'block text-base' : 'text-sm',
            'px-3 py-2 rounded-md font-medium'
          )}
          aria-current={item.current ? 'page' : undefined}
        >
          {item.name}
        </Link>
      ))}

      <Menu as="div" className="ml-3 relative">
        <div>
          <Menu.Button
            className={classNames(
              mobile ? 'block text-base' : 'text-sm',
              'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md font-medium'
            )}
          >
            <span className="sr-only">Open user menu</span>
            <div className="">Admin</div>
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-right absolute left-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
            {[
              { name: 'Removed Items', linkto: '/admin/view_removed_items' },
              { name: 'Manage Orders', linkto: '/admin/manage_orders' },
              { name: 'Create Product', linkto: '/admin/create_product' },
              { name: 'Manage Product', linkto: '/admin/manage_products' },
            ].map((n) => (
              <Menu.Item key={n.name}>
                {({ active }) => (
                  <Link
                    to={n.linkto}
                    className={classNames(
                      active ? 'bg-gray-100' : '',
                      'block px-4 py-2 text-sm text-gray-700'
                    )}
                  >
                    {n.name}
                  </Link>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
    </>
  );
};

export default function Navbar({ setOpenCart }) {
  return (
    <>
      <div className="min-h-full">
        <Disclosure as="nav" className="bg-gray-800">
          {({ open }) => (
            <>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="text-2xl text-white font-bold">
                        <Link to="/">shoppers</Link>
                      </div>
                    </div>
                    <div className="hidden md:block">
                      <div className="ml-10 flex items-baseline space-x-4">
                        <NavigationLink />
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <ShoppingIcon setOpenCart={setOpenCart} />
                  </div>

                  <div className="-mr-2 flex md:hidden">
                    {/* Mobile menu button */}
                    <Disclosure.Button className="bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white">
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <XIcon className="block h-6 w-6" aria-hidden="true" />
                      ) : (
                        <MenuIcon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className="md:hidden">
                <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                  <NavigationLink mobile />
                </div>
                <div className="pt-4 pb-3 border-t border-gray-700">
                  <ShoppingIcon mobile setOpenCart={setOpenCart} />
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
    </>
  );
}
